import React, { useState, useEffect, useCallback } from 'react'
import { jcmdRegistry, JCMDExecution } from '../lib/JCMDRegistry'
import ReactMarkdown from 'react-markdown'
import MarkdownRenderer from './MarkdownRenderer'
import { useNavigate } from 'react-router-dom'

interface JCMDProcessorProps {
  command: string
  description?: string
  commandData?: string
  alreadyCreated?: boolean
  children?: React.ReactNode
}

const JCMDProcessor: React.FC<JCMDProcessorProps> = ({ command, description, commandData, alreadyCreated, children }) => {
  const [countdown, setCountdown] = useState(3)
  const [execution, setExecution] = useState<JCMDExecution | null>(null)
  const [isExecuting, setIsExecuting] = useState(false)
  const [hasExecuted, setHasExecuted] = useState(false)
  const [executionId, setExecutionId] = useState<string | null>(null)
  const navigate = useNavigate()

  const isSupported = jcmdRegistry.isSupported(command)

  // 监听执行状态变化
  useEffect(() => {
    if (!executionId) return

    const checkExecutionStatus = () => {
      const currentExecution = jcmdRegistry.getExecution(executionId)
      if (currentExecution) {
        setExecution(currentExecution)
        
        // 如果执行完成或失败，停止检查
        if (currentExecution.status === 'completed' || currentExecution.status === 'failed') {
          setIsExecuting(false)
        }
      }
    }

    // 立即检查一次
    checkExecutionStatus()

    // 设置定时器定期检查状态
    const interval = setInterval(checkExecutionStatus, 500)

    return () => clearInterval(interval)
  }, [executionId])

  const executeCommand = useCallback(async () => {
    if (hasExecuted || (alreadyCreated && command.toUpperCase() === 'CREATE')) return;

    setIsExecuting(true)
    setHasExecuted(true)

    try {
      const result = await jcmdRegistry.execute(command, description,commandData)
      setExecutionId(result.id)
      setExecution(result)
    } catch (error) {
      console.error('Error executing command:', error)
      setIsExecuting(false)
    }
  }, [hasExecuted, alreadyCreated, command, description, commandData]);

  useEffect(() => {
    if (hasExecuted || (alreadyCreated && command.toUpperCase() === 'CREATE')) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          executeCommand()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [hasExecuted, alreadyCreated, command, executeCommand])



  const getStatusColor = () => {
    if (!isSupported) return 'bg-red-100 border-red-400 text-red-800'
    if (isExecuting || execution?.status === 'executing') return 'bg-blue-100 border-blue-400 text-blue-800'
    if (execution?.status === 'completed') return 'bg-green-100 border-green-400 text-green-800'
    if (execution?.status === 'failed') return 'bg-red-100 border-red-400 text-red-800'
    if (execution?.status === 'pending') return 'bg-yellow-100 border-yellow-400 text-yellow-800'
    return 'bg-gray-100 border-gray-400 text-gray-800'
  }

  const getStatusText = () => {
    if (!isSupported) return '未知指令'
    if (execution?.status === 'pending') return '等待执行'
    if (execution?.status === 'executing' || isExecuting) return '执行中...'
    if (execution?.status === 'completed') return '执行完成'
    if (execution?.status === 'failed') return '执行失败'
    if (countdown > 0) return `${countdown}秒后执行`
    return '准备执行'
  }

  // 获取指令描述
  const getCommandDescription = () => {
    return description
  }

  if (alreadyCreated && command.toUpperCase() === 'CREATE') {
    return (
      <div className="border rounded-lg p-4 mb-4 max-w-full overflow-hidden bg-green-50 border-green-200 text-green-800">
        <div className="text-sm font-medium mb-1">🎉 Agent 已创建成功！</div>
      </div>
    )
  }

  return (
    <div className={`border rounded-lg p-4 mb-4 max-w-full overflow-hidden ${getStatusColor()}`}>
      <div className="flex items-center justify-between mb-2 min-w-0">
        <div className="flex items-center space-x-2 min-w-0 flex-1">
          <span className="font-mono text-sm font-semibold truncate">{command}</span>
          <span className="text-xs px-2 py-1 rounded bg-white bg-opacity-50 flex-shrink-0">
            {getStatusText()}
          </span>
        </div>
        {isSupported && countdown > 0 && !hasExecuted && (
          <div className="text-xs flex-shrink-0 ml-2">
            倒计时: {countdown}s
          </div>
        )}
        {isExecuting && (
          <div className="text-xs flex-shrink-0 ml-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          </div>
        )}
      </div>

      {/* 指令描述 - 所有指令都显示 */}
      <div className="mb-2">
        <div className="text-xs font-semibold mb-1">描述:</div>
        <div className="text-sm bg-white bg-opacity-50 p-2 rounded break-words overflow-hidden">
          {getCommandDescription()}
        </div>
      </div>

      {/* 执行结果 */}
      {isSupported && execution && (
        <div className="mt-2 max-w-full">
          <div className="text-xs font-semibold mb-1">执行结果:</div>
          <div className="text-xs bg-white bg-opacity-50 p-2 rounded overflow-hidden max-w-full">
            {execution.status === 'pending' && (
              <div className="text-yellow-700 break-words">
                ⏳ 等待执行中...
              </div>
            )}
            {execution.status === 'executing' && (
              <div className="text-blue-700 break-words">
                🔄 正在执行...
              </div>
            )}
            {execution.status === 'completed' && (
              <div className="text-green-700 break-words">
                ✓ 执行成功
                {execution.result && (
                  <div className="mt-1">
                    <div className="font-semibold">返回结果:</div>
                    <pre className="text-xs mt-1 bg-gray-50 p-2 rounded overflow-x-auto break-words max-h-40 overflow-y-auto">
                      {JSON.stringify(execution.result, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
            {execution.status === 'failed' && (
              <div className="text-red-700 break-words">
                ✗ 执行失败:
                {typeof execution.error === 'object' && execution.error !== null
                  ? (
                    <pre className="mt-1 bg-gray-50 p-2 rounded overflow-x-auto break-words max-h-40 overflow-y-auto text-xs">
                      {JSON.stringify(execution.error, null, 2)}
                    </pre>
                  )
                  : (
                    <div className="mt-1 break-words overflow-hidden">
                      <div className="bg-red-50 p-2 rounded max-h-40 overflow-y-auto">
                        <span className="break-all whitespace-pre-wrap text-xs">{String(execution.error)}</span>
                      </div>
                    </div>
                  )
                }
              </div>
            )}
          </div>
        </div>
      )}

      {/* 手动执行按钮 */}
      {isSupported && !hasExecuted && (
        <button
          onClick={executeCommand}
          className="mt-2 px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors"
        >
          立即执行
        </button>
      )}

      {/* 重新执行按钮 */}
      {isSupported && hasExecuted && execution && (execution.status === 'completed' || execution.status === 'failed') && (
        <button
          onClick={() => {
            setHasExecuted(false)
            setExecution(null)
            setExecutionId(null)
            setCountdown(3)
          }}
          className="mt-2 px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 transition-colors"
        >
          重新执行
        </button>
      )}
    </div>
  )
}

export default JCMDProcessor